import * as React from 'react'
import { useLocale } from './useLocal'
import { ErrorType } from '../types/address'
import { containsEmojis, isIndianNumber, isNumeric, isValidEmail, regexPatterns } from '../utils/validations'
import { FormatterTypes, LengthLimitType, ValidationItem } from '../types/validations'
import { capitalizeFirstCharacter } from '../utils/helpers'
import { AdditionalPocContactErrorsType } from '../types/settings'

export type ValidationSchema = Record<string, ValidationItem>

interface UseFormProps<T> {
  initialState: T
  validationSchema: ValidationSchema
  skipValidation?: boolean
}

export default function useForm<T extends Record<string, unknown>>({
  initialState,
  validationSchema,
  skipValidation = false
}: UseFormProps<T>) {
  const [values, setValues] = React.useState(initialState)
  const [errors, setErrors] = React.useState<Record<string, ErrorType | undefined>>({})
  const [touched, setTouched] = React.useState<Record<string, boolean>>({})

  const { t } = useLocale()

  const setValueOf = (field: string, value: unknown) => {
    setValues((values: any) => ({ ...values, [field]: value }))
  }

  const format = (value: any = '', formatters: FormatterTypes[]) => {
    const formattedValue: string = formatters.reduce((formatted: string, formatter) => {
      switch (formatter) {
        case 'UPPERCASE': {
          return formatted.toUpperCase()
        }
        case 'FIRST_CHAR_LOWERCASE': {
          return formatted.charAt(0).toLowerCase() + formatted.slice(1)
        }
        case 'CAPITALIZE': {
          return capitalizeFirstCharacter(formatted)
        }
        case 'PHONE': {
          if (isIndianNumber(formatted) && formatted[3] === '0') {
            return formatted.slice(0, 3) + formatted.slice(4)
          }
          return formatted
        }
        case 'CLEAN_EMOJIS': {
          return formatted.replace(regexPatterns.hasEmoji, '')
        }
        case 'NUMERIC': {
          return formatted.replace(regexPatterns.numeric, '')
        }
        case 'LETTERS_WITH_NO_SPACE': {
          return formatted.replaceAll(regexPatterns.space, '')
        }
        case 'LETTERS_ONLY': {
          return formatted.replace(regexPatterns.lettersOnly, '')
        }
        case 'LETTERS_AND_PERIOD': {
          return formatted.replace(regexPatterns.lettersAndPeriod, '')
        }
        case 'ALPHA_NUMERIC': {
          return formatted.replace(regexPatterns.alphaNumeric, '')
        }
        default: {
          if (typeof formatter === 'function') {
            return formatter(formatted, values)
          }
          return formatted
        }
      }
    }, value)
    return formattedValue
  }

  const validateField = (field: string, value: any) => {
    value = value?.trim()
    const fieldSchema = validationSchema[field]
    const validationKeys = Object.keys(fieldSchema)

    for (const key of validationKeys) {
      const schemaItem = fieldSchema[key as keyof ValidationItem]
      if (!containsEmojis(value)) {
        value = format(value, ['CLEAN_EMOJIS'])
      }
      switch (key) {
        case 'formatters': {
          format(value, schemaItem as FormatterTypes[])
          break
        }
        case 'type': {
          const type = schemaItem
          // if (type === "string" && typeof value !== "string")
          //   return { status: true, message: "Please enter a valid string" };
          if (type === 'number' && typeof value === 'number' && !isNaN(value))
            return { status: true, message: 'Please enter a valid number' }
          break
        }
        case 'required': {
          if (!Boolean(value))
            return {
              status: true,
              message: schemaItem as string
            }
          break
        }
        case 'min': {
          if (value.length < (schemaItem as LengthLimitType)?.limit)
            return {
              status: true,
              message: (schemaItem as LengthLimitType).message
            }
          break
        }
        case 'max': {
          if (value.length > (schemaItem as LengthLimitType).limit)
            return {
              status: true,
              message: (schemaItem as LengthLimitType).message
            }
          break
        }
        case 'length': {
          if (value.length !== (schemaItem as LengthLimitType).limit)
            return {
              status: true,
              message: (schemaItem as LengthLimitType).message
            }
          break
        }
        case 'numeric': {
          if (!isNumeric(value)) {
            return {
              status: true,
              message: schemaItem as string
            }
          }
          break
        }
        case 'onlyLetters': {
          if (regexPatterns.lettersOnly.test(value)) {
            return {
              status: true,
              message: schemaItem as string
            }
          }
          break
        }
        case 'onlyLettersAndPeriod': {
          if (regexPatterns.lettersAndPeriod.test(value)) {
            return {
              status: true,
              message: schemaItem as string
            }
          }
          break
        }
        case 'notSpecialCharactersOrNumbersOnly': {
          if (regexPatterns.specialCharactersAndNumbersOnly.test(value)) {
            return {
              status: true,
              message: schemaItem as string
            }
          }
          break
        }
        case 'email': {
          if (!isValidEmail(value)) {
            return {
              status: true,
              message: schemaItem as string
            }
          }
          break
        }
        case 'phone': {
          if (isIndianNumber(value)) {
            if (value.length !== 13) {
              return {
                status: true,
                message: t('indian_phone_number_digits_limit') as string
              }
            }
            if (value[3] < 6) {
              return {
                status: true,
                message: schemaItem as string
              }
            }
          }
        }
        case 'when': {
          if (typeof schemaItem === 'function') {
            return schemaItem(value, values)
          }
          break
        }
        case 'noPlusSign': {
          if (value.includes('+')) {
            return {
              status: true,
              message: t('no_plus_sign_allowed') as string
            }
          }
          break
        }
        default: {
          throw new Error('unexpected schema key, ' + key)
        }
      }
    }
  }

  const validate = (
    values: any,
    scrollIntoError: (errors: any, pocContactsErrors: AdditionalPocContactErrorsType[]) => void,
    {
      hasPocContactErrors = false,
      pocContactsErrors
    }: { hasPocContactErrors: boolean; pocContactsErrors: AdditionalPocContactErrorsType[] }
  ) => {
    const validationKeys = Object.keys(validationSchema)
    const errors: Record<string, ErrorType | undefined> = {}
    let hasError = false
    for (const key of validationKeys) {
      if (Boolean(skipValidation) && (key === 'state' || key === 'state_code' || key === 'zip')) {
        continue
      }
      if (key === 'identification_number' && values.tax_type === 'UNREGISTERED_BUSINESS') {
        continue
      }
      const error = validateField(key, values[key])
      if (error) {
        errors[key] = error
        hasError = true
      }
    }
    setErrors(errors)
    if (hasError || hasPocContactErrors) scrollIntoError(errors, pocContactsErrors)
    return !(hasError || hasPocContactErrors)
  }

  function handleSubmit(
    callback: (values: any) => void,
    scrollIntoError: (errors: any, pocContactsErrors: AdditionalPocContactErrorsType[]) => void,
    additionalPocContactsErrors: {
      hasPocContactErrors: boolean
      pocContactsErrors: AdditionalPocContactErrorsType[]
    }
  ) {
    const isValid = validate(values, scrollIntoError, additionalPocContactsErrors)
    if (!isValid) {
      return
    }
    callback(values)
  }

  const handleFieldChange = (fieldName: string, value: unknown) => {
    setTouched(touched => ({ ...touched, [fieldName]: true }))
    const fieldSchema = validationSchema[fieldName]
    value = format(value, ['CLEAN_EMOJIS'])
    if (fieldSchema.formatters) {
      value = format(value, fieldSchema.formatters)
    }
    if (errors[fieldName] && !validateField(fieldName, value)) {
      setErrors(errs => ({ ...errs, [fieldName]: undefined }))
    }
    setValueOf(fieldName, value)
  }

  const inputProps = (
    fieldName: string,
    config?: {
      onChange?: (e: any) => void
      onBlur?: (event: React.FocusEvent<HTMLElement>) => void
      onFocus?: (event: React.FocusEvent<HTMLElement>) => void
    }
  ) => {
    const hasCustomOnChange = config?.onChange != null

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      handleFieldChange(fieldName, value)
    }

    const onBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
      if (config?.onBlur != null) {
        config.onBlur(event)
      }

      if (!touched[fieldName]) {
        return
      }

      const error = validateField(fieldName, values[fieldName])

      if (error) {
        setErrors(errs => ({ ...errs, [fieldName]: error }))
      } else if (errors[fieldName]) {
        setErrors(errs => ({ ...errs, [fieldName]: undefined }))
      }
    }

    const onFocus = (event: React.FocusEvent<HTMLElement>): void => {
      if (config?.onFocus != null) {
        config.onFocus(event)
      }
    }

    return {
      name: fieldName,
      id: fieldName,
      onChange: (hasCustomOnChange ? config.onChange : onChange) as (e: any) => void,
      onBlur,
      onFocus,
      value: values[fieldName] as string
    }
  }

  return {
    handleSubmit,
    setValues,
    setErrors,
    setValueOf,
    handleFieldChange,
    state: {
      values,
      errors
    },
    inputProps
  }
}
